
import axios from 'axios';


const API_BASE_URL = '/api/v1'; 

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});


export const getSystemLatestBusinessDate = async () => {
  try {
    const response = await api.get('/system/latest-business-date');
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch system latest business date:', error);
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    return {
      latestDate: today.toISOString().split('T')[0],
      firstDayOfMonth: firstDay.toISOString().split('T')[0],
      defaultDateRange: {
        startDate: firstDay.toISOString().split('T')[0],
        endDate: today.toISOString().split('T')[0],
      },
    };
  }
};


export const getStoreLatestBusinessDate = async (salesforceId: string) => {
  try {
    const response = await api.get(`/dashboard/salesforce/${salesforceId}/latest-business-date`);
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch store latest business date:', error);
    return getSystemLatestBusinessDate();
  }
};





export const getDashboardKpiOnly = async (
  salesforceId: string,
  params: {
    startDate: string;
    endDate: string;
    dayOfWeek?: number[];
    hour?: number[];
    behavior?: string[];
    diningGroup?: string[];
    menuGroup?: string[];
    menuCategory?: string[];
  }
) => {
  try {
    const queryParams: any = {
      startDate: params.startDate,
      endDate: params.endDate,
    };

    if (params.dayOfWeek && params.dayOfWeek.length > 0) {
      queryParams.days = params.dayOfWeek.join(',');
    }

    if (params.hour && params.hour.length > 0) {
      queryParams.hours = params.hour.join(',');
    }

    if (params.behavior && params.behavior.length > 0) {
      queryParams.behaviors = params.behavior.join(',');
    }

    if (params.diningGroup && params.diningGroup.length > 0) {
      queryParams.diningGroups = params.diningGroup.join(',');
    }

    if (params.menuGroup && params.menuGroup.length > 0) {
      queryParams.categories = params.menuGroup.join(',');
    } else if (params.menuCategory && params.menuCategory.length > 0) {
      queryParams.categories = params.menuCategory.join(',');
    }

    const response = await api.get(`/dashboard/salesforce/${salesforceId}/kpi`, {
      params: queryParams,
    });
    return response.data;
  } catch (error: any) {
    throw error;
  }
};


export const getDashboardChartsOnly = async (
  salesforceId: string,
  params: {
    startDate: string;
    endDate: string;
    dayOfWeek?: number[];
    hour?: number[];
    behavior?: string[];
    diningGroup?: string[];
    menuGroup?: string[];
    menuCategory?: string[];
  }
) => {
  try {
    const queryParams: any = {
      startDate: params.startDate,
      endDate: params.endDate,
    };

    if (params.dayOfWeek && params.dayOfWeek.length > 0) {
      queryParams.days = params.dayOfWeek.join(',');
    }

    if (params.hour && params.hour.length > 0) {
      queryParams.hours = params.hour.join(',');
    }

    if (params.behavior && params.behavior.length > 0) {
      queryParams.behaviors = params.behavior.join(',');
    }

    if (params.diningGroup && params.diningGroup.length > 0) {
      queryParams.diningGroups = params.diningGroup.join(',');
    }

    if (params.menuGroup && params.menuGroup.length > 0) {
      queryParams.categories = params.menuGroup.join(',');
    } else if (params.menuCategory && params.menuCategory.length > 0) {
      queryParams.categories = params.menuCategory.join(',');
    }

    const response = await api.get(`/dashboard/salesforce/${salesforceId}/charts`, {
      params: queryParams,
    });
    return response.data;
  } catch (error: any) {
    throw error;
  }
};


export const getDashboardData = async (
  salesforceId: string,
  params: {
    startDate: string;
    endDate: string;
    dayOfWeek?: number[];
    hour?: number[];
    behavior?: string[];
    diningGroup?: string[];
    menuGroup?: string[];
    menuCategory?: string[];
  }
) => {
  try {
    const queryParams: any = {
      startDate: params.startDate,
      endDate: params.endDate,
    };

    if (params.dayOfWeek && params.dayOfWeek.length > 0) {
      queryParams.days = params.dayOfWeek.join(',');
    }

    if (params.hour && params.hour.length > 0) {
      queryParams.hours = params.hour.join(',');
    }

    if (params.behavior && params.behavior.length > 0) {
      queryParams.behaviors = params.behavior.join(',');
    }

    if (params.diningGroup && params.diningGroup.length > 0) {
      queryParams.diningGroups = params.diningGroup.join(',');
    }

    if (params.menuGroup && params.menuGroup.length > 0) {
      queryParams.categories = params.menuGroup.join(',');
    } else if (params.menuCategory && params.menuCategory.length > 0) {
      queryParams.categories = params.menuCategory.join(',');
    }

    const response = await api.get(`/dashboard/salesforce/${salesforceId}/all`, {
      params: queryParams,
    });
    return response.data;
  } catch (error: any) {
    throw error;
  }
};


export interface StoreValidationResponse {
  status: 'ready' | 'no-store' | 'no-data' | 'invalid' | 'error';
  message: string;
  salesforceId: string;
  storeGuid?: string;
  storeInfo?: {
    Store_name: string;
    Location: string;
    StoreType: string;
  };
  hasStore: boolean;
  hasData: boolean;
  error?: string;
}


export const validateStoreStatus = async (salesforceId: string): Promise<StoreValidationResponse> => {
  try {
    const response = await api.get(`/dashboard/salesforce/${salesforceId}/validate`);
    return response.data;
  } catch (error: any) {
    console.error('Failed to validate store status:', error);
    
    
    if (error.response) {
      if (error.response.status === 404) {
        return {
          status: 'no-store',
          message: 'Store not found',
          salesforceId,
          hasStore: false,
          hasData: false
        };
      } else if (error.response.status === 400) {
        return {
          status: 'invalid',
          message: 'Invalid store ID format',
          salesforceId,
          hasStore: false,
          hasData: false
        };
      } else {
        return {
          status: 'error',
          message: 'Server error while validating store',
          salesforceId,
          hasStore: false,
          hasData: false,
          error: error.response.data?.message || 'Unknown server error'
        };
      }
    } else if (error.request) {
      return {
        status: 'error',
        message: 'Network error - unable to reach server',
        salesforceId,
        hasStore: false,
        hasData: false,
        error: 'Network error'
      };
    } else {
      return {
        status: 'error',
        message: 'Unexpected error during validation',
        salesforceId,
        hasStore: false,
        hasData: false,
        error: error.message || 'Unknown error'
      };
    }
  }
};

export const getDiningBehaviorData = async (
  salesforceId: string,
  params: { startDate?: string; endDate?: string } = {}
) => {
  try {
    const response = await api.get(`/dashboard/salesforce/${salesforceId}/dining-behavior`, {
      params,
    });
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch dining behavior data:', error);
    throw error;
  }
};


export const getDiningGroupData = async (
  salesforceId: string,
  params: { startDate?: string; endDate?: string } = {}
) => {
  try {
    const response = await api.get(`/dashboard/salesforce/${salesforceId}/dining-group`, {
      params,
    });
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch dining group data:', error);
    throw error;
  }
};


export const getDayOfWeekHourlyData = async (
  salesforceId: string,
  params: { startDate?: string; endDate?: string; dayOfWeek?: number[]; hour?: number[] } = {}
) => {
  try {
    const queryParams: any = {
      startDate: params.startDate,
      endDate: params.endDate,
    };
    if (params.dayOfWeek && params.dayOfWeek.length > 0) {
      queryParams.dayOfWeek = params.dayOfWeek.join(',');
    }
    if (params.hour && params.hour.length > 0) {
      queryParams.hour = params.hour.join(',');
    }
    const response = await api.get(`/dashboard/salesforce/${salesforceId}/dow-hourly`, {
      params: queryParams,
    });
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch day of week hourly data:', error);
    throw error;
  }
};


export const getHourlyBehaviorData = async (
  salesforceId: string,
  params: { startDate?: string; endDate?: string; hour?: number[]; behavior?: string[] } = {}
) => {
  try {

    const queryParams: any = {
      startDate: params.startDate,
      endDate: params.endDate,
    };
    if (params.hour && params.hour.length > 0) {
      queryParams.hour = params.hour.join(',');
    }
    if (params.behavior && params.behavior.length > 0) {
      queryParams.behavior = params.behavior.join(',');
    }
    const response = await api.get(`/dashboard/salesforce/${salesforceId}/hourly-behavior`, {
      params: queryParams,
    });
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch hourly behavior data:', error);
    throw error;
  }
};

export const getHourlyDiningGroupData = async (
  salesforceId: string,
  params: { startDate?: string; endDate?: string; hour?: number[]; diningGroup?: string[] } = {}
) => {
  try {
    const queryParams: any = {
      startDate: params.startDate,
      endDate: params.endDate,
    };
    if (params.hour && params.hour.length > 0) {
      queryParams.hour = params.hour.join(',');
    }
    if (params.diningGroup && params.diningGroup.length > 0) {
      queryParams.diningGroup = params.diningGroup.join(',');
    }
    const response = await api.get(`/dashboard/salesforce/${salesforceId}/hourly-dining-group`, {
      params: queryParams,
    });
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch hourly dining group data:', error);
    throw error;
  }
};


export const getDayOfWeekBehaviorData = async (
  salesforceId: string,
  params: { startDate?: string; endDate?: string; dayOfWeek?: number[]; behavior?: string[] } = {}
) => {
  try {
    const queryParams: any = {
      startDate: params.startDate,
      endDate: params.endDate,
    };
    if (params.dayOfWeek && params.dayOfWeek.length > 0) {
      queryParams.dayOfWeek = params.dayOfWeek.join(',');
    }
    if (params.behavior && params.behavior.length > 0) {
      queryParams.behavior = params.behavior.join(',');
    }
    const response = await api.get(`/dashboard/salesforce/${salesforceId}/dow-behavior`, {
      params: queryParams,
    });
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch day of week behavior data:', error);
    throw error;
  }
};


export const getDayOfWeekDiningGroupData = async (
  salesforceId: string,
  params: { startDate?: string; endDate?: string; dayOfWeek?: number[]; diningGroup?: string[] } = {}
) => {
  try {
    const queryParams: any = {
      startDate: params.startDate,
      endDate: params.endDate,
    };
    if (params.dayOfWeek && params.dayOfWeek.length > 0) {
      queryParams.dayOfWeek = params.dayOfWeek.join(',');
    }
    if (params.diningGroup && params.diningGroup.length > 0) {
      queryParams.diningGroup = params.diningGroup.join(',');
    }
    const response = await api.get(`/dashboard/salesforce/${salesforceId}/dow-dining-group`, {
      params: queryParams,
    });
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch day of week dining group data:', error);
    throw error;
  }
};


export const getDayOfWeekMenuData = async (
  salesforceId: string,
  params: { startDate?: string; endDate?: string; dayOfWeek?: number[]; menuGroup?: string[] } = {}
) => {
  try {
    const queryParams: any = {
      startDate: params.startDate,
      endDate: params.endDate,
    };
    if (params.dayOfWeek && params.dayOfWeek.length > 0) {
      queryParams.dayOfWeek = params.dayOfWeek.join(',');
    }
    if (params.menuGroup && params.menuGroup.length > 0) {
      queryParams.menuGroup = params.menuGroup.join(',');
    }
    const response = await api.get(`/dashboard/salesforce/${salesforceId}/dow-menu`, {
      params: queryParams,
    });
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch day of week menu data:', error);
    throw error;
  }
};


export const getHourlyMenuData = async (
  salesforceId: string,
  params: { startDate?: string; endDate?: string; hour?: number[]; menuGroup?: string[] } = {}
) => {
  try {
    const queryParams: any = {
      startDate: params.startDate,
      endDate: params.endDate,
    };
    if (params.hour && params.hour.length > 0) {
      queryParams.hour = params.hour.join(',');
    }
    if (params.menuGroup && params.menuGroup.length > 0) {
      queryParams.menuGroup = params.menuGroup.join(',');
    }
    const response = await api.get(`/dashboard/salesforce/${salesforceId}/hourly-menu`, {
      params: queryParams,
    });
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch hourly menu data:', error);
    throw error;
  }
};

export const getBehaviorMenuData = async (
  salesforceId: string,
  params: { startDate?: string; endDate?: string; behavior?: string[]; menuGroup?: string[] } = {}
) => {
  try {
    const queryParams: any = {
      startDate: params.startDate,
      endDate: params.endDate,
    };
    if (params.behavior && params.behavior.length > 0) {
      queryParams.behavior = params.behavior.join(',');
    }
    if (params.menuGroup && params.menuGroup.length > 0) {
      queryParams.menuGroup = params.menuGroup.join(',');
    }
    const response = await api.get(`/dashboard/salesforce/${salesforceId}/behavior-menu`, {
      params: queryParams,
    });
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch behavior menu data:', error);
    throw error;
  }
};


export const getDiningGroupMenuData = async (
  salesforceId: string,
  params: { startDate?: string; endDate?: string; diningGroup?: string[]; menuGroup?: string[] } = {}
) => {
  try {
    const queryParams: any = {
      startDate: params.startDate,
      endDate: params.endDate,
    };
    if (params.diningGroup && params.diningGroup.length > 0) {
      queryParams.diningGroup = params.diningGroup.join(',');
    }
    if (params.menuGroup && params.menuGroup.length > 0) {
      queryParams.menuGroup = params.menuGroup.join(',');
    }
    const response = await api.get(`/dashboard/salesforce/${salesforceId}/dining-group-menu`, {
      params: queryParams,
    });
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch dining group menu data:', error);
    throw error;
  }
};


export const getDiningOptionData = async (
  salesforceId: string,
  params: { startDate?: string; endDate?: string; groupBy?: string } = {}
) => {
  try {
    const response = await api.get(`/dashboard/salesforce/${salesforceId}/dining-options`, {
      params,
    });
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch dining option data:', error);
    throw error;
  }
};


export const getDayOfWeekData = async (
  salesforceId: string,
  params: { startDate?: string; endDate?: string } = {}
) => {
  try {
    const response = await api.get(`/dashboard/salesforce/${salesforceId}/day-of-week`, {
      params,
    });
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch day of week data:', error);
    throw error;
  }
};


export const getHourlyData = async (
  salesforceId: string,
  params: { startDate?: string; endDate?: string } = {}
) => {
  try {
    const response = await api.get(`/dashboard/salesforce/${salesforceId}/hourly`, {
      params,
    });
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch hourly data:', error);
    throw error;
  }
};


export const getMenuGroupData = async (
  salesforceId: string,
  params: { startDate?: string; endDate?: string } = {}
) => {
  try {
    const response = await api.get(`/dashboard/salesforce/${salesforceId}/menu-groups`, {
      params,
    });
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch menu group data:', error);
    throw error;
  }
};


export const getTopMenuItems = async (
  salesforceId: string,
  params: { startDate?: string; endDate?: string; limit?: number } = {}
) => {
  try {
    const response = await api.get(`/dashboard/salesforce/${salesforceId}/top-menu-items`, {
      params,
    });
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch top menu items:', error);
    throw error;
  }
};


export const getBottomMenuItems = async (
  salesforceId: string,
  params: { startDate?: string; endDate?: string; limit?: number } = {}
) => {
  try {
    const response = await api.get(`/dashboard/salesforce/${salesforceId}/bottom-menu-items`, {
      params,
    });
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch bottom menu items:', error);
    throw error;
  }
};


export const getMenuCategories = async (
  salesforceId: string,
  params: { startDate?: string; endDate?: string } = {}
) => {
  try {
    const response = await api.get(`/dashboard/salesforce/${salesforceId}/menu-categories`, {
      params,
    });
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch menu categories:', error);
    if (error.response && error.response.status === 400) {
      console.warn('Menu categories API not implemented yet (400 error)');
    }
    throw error;
  }
};

export const getMenuCategoryData = async (
  salesforceId: string,
  params: {
    startDate?: string;
    endDate?: string;
    menuGroup: string;
  }
): Promise<any[]> => {
  try {
    const response = await api.get(`/dashboard/salesforce/${salesforceId}/menu-group-categories`, {
      params,
    });
    return response.data;
  } catch (error) {
    console.error('메뉴 카테고리 데이터 조회 실패:', error);
    throw error;
  }
};


export const getMonthlyMenuItemsData = async (salesforceId: string) => {
  try {
    const response = await api.get(`/dashboard/salesforce/${salesforceId}/monthly-menu-items-data`);
    return response.data;
  } catch (error) {
    console.error('월별 메뉴 아이템 데이터 조회 오류:', error);
    throw error;
  }
};


export const getMenuCategoryMonthlyTrend = async (salesforceId: string): Promise<any> => {
  try {
    const response = await api.get(`/dashboard/salesforce/${salesforceId}/menu-category-monthly-trend`);
    return response.data;
  } catch (error) {
    console.error('메뉴 카테고리 월별 추이 데이터 조회 실패:', error);
    throw error;
  }
};

export const getMenuItemsMonthlyTrend = async (
  salesforceId: string,
  menuGroup: string,
  menuCategory: string
): Promise<any> => {
  try {
    const response = await api.get(`/dashboard/salesforce/${salesforceId}/menu-items-monthly-trend`, {
      params: { menuGroup, menuCategory },
    });
    return response.data;
  } catch (error) {
    console.error('메뉴 아이템 월별 추이 데이터 조회 실패:', error);
    throw error;
  }
};


export const getMenuCategoriesForTrend = async (salesforceId: string): Promise<any> => {
  try {
    const response = await api.get(`/dashboard/salesforce/${salesforceId}/menu-categories-for-trend`);
    return response.data;
  } catch (error) {
    console.error('메뉴 카테고리 목록 조회 실패:', error);
    throw error;
  }
};

export const getMonthlyHourlyTrend = async (salesforceId: string): Promise<any> => {
  try {
    const response = await api.get(`/dashboard/salesforce/${salesforceId}/monthly-hourly-trend`);
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch monthly hourly trend data:', error);
    throw error;
  }
};


export const getDiningGroupMonthlyTrend = async (salesforceId: string): Promise<any[]> => {
  try {
    const response = await api.get(`/dashboard/salesforce/${salesforceId}/dining-group-monthly-trend`);
    return response.data;
  } catch (error) {
    console.error('Error fetching dining group monthly trend:', error);
    throw error;
  }
};


export const getDiningOptionsMonthlyTrend = async (salesforceId: string, diningGroup: string): Promise<any> => {
  try {
    const response = await api.get(`/dashboard/salesforce/${salesforceId}/dining-options-monthly-trend`, {
      params: { diningGroup },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching dining options monthly trend:', error);
    throw error;
  }
};


export const getDiningBehaviorMonthlyTrend = async (salesforceId: string): Promise<any[]> => {
  try {
    const response = await api.get(`/dashboard/salesforce/${salesforceId}/dining-behavior-monthly-trend`);
    return response.data;
  } catch (error) {
    console.error('Error fetching dining behavior monthly trend:', error);
    throw error;
  }
};


export const getBehaviorOptionsMonthlyTrend = async (salesforceId: string, behavior: string) => {
  const response = await api.get(`/dashboard/salesforce/${salesforceId}/behavior-options-monthly-trend`, {
    params: { behavior },
  });
  return response.data;
};


export const buildFilterParams = (filters: {
  dateRange: { startDate: string; endDate: string };
  dayOfWeek?: { selectedDays: number[]; allDays: boolean };
  time?: { selectedHours: number[]; allHours: boolean };
  diningBehavior?: { selectedBehaviors: string[]; allBehaviors: boolean };
}) => {

  const params: {
    startDate: string;
    endDate: string;
    dayOfWeek?: number[];
    hour?: number[];
    behavior?: string[];
    diningGroup?: string[];
    menuGroup?: string[];
  } = {
    startDate: filters.dateRange.startDate,
    endDate: filters.dateRange.endDate,
  };

  if (filters.dayOfWeek && !filters.dayOfWeek.allDays && filters.dayOfWeek.selectedDays.length > 0) {
    params.dayOfWeek = filters.dayOfWeek.selectedDays;
  }

  if (filters.time && !filters.time.allHours && filters.time.selectedHours.length > 0) {
    params.hour = filters.time.selectedHours;
  }

  if (filters.diningBehavior && !filters.diningBehavior.allBehaviors && filters.diningBehavior.selectedBehaviors.length > 0) {
    params.behavior = filters.diningBehavior.selectedBehaviors;
  }

  return params;
};


export const getYearlyTrendData = async (salesforceId: string) => {
  try {
    
    const response = await api.get(
      `/dashboard/salesforce/${salesforceId}/yearly-trend`
    );
    
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch yearly trend data:', error);
    if (error.response) {
      console.error('Error response:', error.response.data);
    }
    throw error;
  }
};


export const getQuarterlyTrendData = async (salesforceId: string) => {
  try {
    const response = await api.get(
      `/dashboard/salesforce/${salesforceId}/quarterly-trend`
    );
    
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch quarterly trend data:', error);
    if (error.response) {
      console.error('Error response:', error.response.data);
    }
    throw error;
  }
};


export const getMonthComparisonData = async (
  salesforceId: string,
  params: {
    dayOfWeek?: number[];
    hour?: number[];
    behavior?: string[];
  } = {}
) => {
  try {
    const queryParams: any = {};
    
    if (params.dayOfWeek && params.dayOfWeek.length > 0) {
      queryParams.dayOfWeek = params.dayOfWeek.join(',');
    }
    if (params.hour && params.hour.length > 0) {
      queryParams.hour = params.hour.join(',');
    }
    if (params.behavior && params.behavior.length > 0) {
      queryParams.behavior = params.behavior.join(',');
    }
    
    const response = await api.get(
      `/dashboard/salesforce/${salesforceId}/month-comparison`,
      { params: queryParams }
    );
    
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch month comparison data:', error);
    if (error.response) {
      console.error('Error response:', error.response.data);
    }
    throw error;
  }
};

export const getMonthlyDayOfWeekTrend = async (salesforceId: string): Promise<any> => {
  try {
    const response = await api.get(`/dashboard/salesforce/${salesforceId}/monthly-day-of-week-trend`);
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch monthly day of week trend data:', error);
    throw error;
  }
};



export const getKpiMonthlyTrend = async (
  salesforceId: string,
  params: {
    startDate?: string;
    endDate?: string;
    dayOfWeek?: number[];
    hour?: number[];
    behavior?: string[];
  } = {}
): Promise<any> => {
  try {
    const queryParams: any = {};
    
    if (params.startDate) queryParams.startDate = params.startDate;
    if (params.endDate) queryParams.endDate = params.endDate;
    if (params.dayOfWeek && params.dayOfWeek.length > 0) {
      queryParams.dayOfWeek = params.dayOfWeek.join(',');
    }
    if (params.hour && params.hour.length > 0) {
      queryParams.hour = params.hour.join(',');
    }
    if (params.behavior && params.behavior.length > 0) {
      queryParams.behavior = params.behavior.join(',');
    }
    
    const response = await api.get(`/dashboard/salesforce/${salesforceId}/monthly-trend`, {
      params: queryParams,
    });
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch KPI monthly trend data:', error);
    throw error;
  }
};

export default api;
